import clsx from "clsx";
import { useVirtualizer } from "hooks";
import { last } from "lodash";
import { array, bool, func, number, object, string } from "prop-types";
import { forwardRef, useImperativeHandle, useRef } from "react";

import classes from "./VirtualizedItems.module.scss";

const getNextAnimationFrame = (onFrameId = noop) =>
  new Promise((resolve) => {
    const frameId = requestAnimationFrame(resolve);
    onFrameId(frameId);
  });

const VirtualizedItems = forwardRef(
  (
    {
      children,
      className,
      data = [],
      estimateSize = () => 40,
      horizontal = false,
      initialOffset = 0,
      itemClassName,
      maxContent,
      onScrollChange,
      overscan = 10,
      smoothScrollThreshold = 30,
      virtualizerRef,
    },
    forwardedRef
  ) => {
    const ref = useRef();
    const scrollRef = forwardedRef || ref;
    const listSize = maxContent ? "max-content" : "100%";
    const rowCount = data.length;

    const pendingScrollRef = useRef(null);
    const scrollGenerationRef = useRef(0);

    const rowVirtualizer = useVirtualizer({
      count: rowCount,
      estimateSize,
      horizontal,
      initialOffset,
      onChange: onScrollChange
        ? (instance) => onScrollChange(instance.scrollOffset)
        : undefined,
      overscan,
      scrollRef,
    });
    const cancelPendingScroll = () => {
      scrollGenerationRef.current += 1;

      if (pendingScrollRef.current) {
        cancelAnimationFrame(pendingScrollRef.current);
        pendingScrollRef.current = null;
      }
    };

    const nextFrame = async () => {
      await getNextAnimationFrame((frameId) => {
        pendingScrollRef.current = frameId;
      });

      pendingScrollRef.current = null;
    };

    const items = rowVirtualizer.getVirtualItems();
    const totalSize = rowVirtualizer.getTotalSize();
    const translatePx = items[0]?.start ?? 0;
    const translateBy = horizontal ? "translateX" : "translateY";

    const scrollToRowByIndex = async (index, options = {}) => {
      const align = options.align || "center";
      const behavior = options.behavior || "smooth";
      const threshold = options.threshold ?? smoothScrollThreshold;

      cancelPendingScroll();
      const generation = scrollGenerationRef.current;

      if (!rowVirtualizer) return;

      const visibleIndices = items.map((i) => i.index);
      const firstVisible = visibleIndices[0];
      const lastVisible = last(visibleIndices);

      const isBelow = index > lastVisible;
      const distance = isBelow ? index - lastVisible : firstVisible - index;

      const shouldStage =
        behavior === "smooth" &&
        visibleIndices.length > 0 &&
        threshold > 0 &&
        distance > threshold;

      if (!shouldStage) {
        rowVirtualizer.scrollToIndex(index, { align, behavior });
        return;
      }

      // Jump instantly to `threshold` rows short, then animate the last stretch.
      const stagingIndex = Math.min(
        Math.max(index + (isBelow ? -threshold : threshold), 0),
        Math.max(rowCount - 1, 0)
      );

      rowVirtualizer.scrollToIndex(stagingIndex, {
        align,
        behavior: "auto",
      });

      await nextFrame();
      await nextFrame();

      if (generation !== scrollGenerationRef.current) return;

      rowVirtualizer.scrollToIndex(index, { align, behavior: "smooth" });
    };

    useImperativeHandle(virtualizerRef, () => ({
      ...rowVirtualizer,
      scrollToRowByIndex,
    }));

    return (
      <div
        className={clsx(classes["virtualized_items__content"], className, {
          [classes["virtualized_items__content--horizontal"]]: horizontal,
        })}
        data-testid="virtualized-list"
        ref={scrollRef}
      >
        <div
          className={classes["virtualized_items__virtual_list"]}
          style={{
            [horizontal ? "width" : "height"]: `${totalSize}px`,
          }}
        >
          <div
            className={classes["virtualized_items__virtual_list__start"]}
            style={{
              [horizontal ? "height" : "width"]: listSize,
              transform: `${translateBy}(${translatePx}px)`,
            }}
          >
            {items.map((virtualItem) => {
              const category = data[virtualItem.index];
              return (
                <div
                  className={clsx(
                    classes["virtualized_items__virtual_list__item"],
                    itemClassName
                  )}
                  data-index={virtualItem.index}
                  key={virtualItem.key}
                  ref={rowVirtualizer.measureElement}
                >
                  {children(category, virtualItem, rowVirtualizer)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
);

VirtualizedItems.displayName = "VirtualizedItems";

VirtualizedItems.propTypes = {
  children: func.isRequired,
  className: string,
  data: array,
  estimateSize: func,
  horizontal: bool,
  initialOffset: number,
  itemClassName: string,
  maxContent: bool,
  onScrollChange: func,
  overscan: number,
  smoothScrollThreshold: number,
  virtualizerRef: object,
};

export default VirtualizedItems;
